// The apply endpoints (POST /api/jobs/:id/apply, POST /api/talent-network/submit)
// answer with an error code plus an English message. The helpers used to throw
// a bare Error with that message, so an Arabic user saw English for every apply
// failure (tracker X4, from #589). Known codes now render translated copy on
// every apply surface; anything unknown keeps the server message, which is
// still the most specific thing we have.

/**
 * An apply request the API refused. `code` is the server's machine code
 * (`APIError.code`), kept so surfaces can route on it; `message` is the
 * server's English text, the fallback when the code is not mapped.
 */
export class ApplyRequestError extends Error {
  readonly code: string;
  readonly details: Record<string, unknown> | undefined;

  constructor(
    code: string,
    message: string,
    details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ApplyRequestError';
    this.code = code;
    this.details = details;
  }
}

/**
 * Server codes with translated copy, keyed to the seeker namespace the apply
 * sheets bind `t` to (`common:` reaches the shared namespace). The three CV
 * refusals are the ones the API names on a new submission: a CV it could not
 * read (B11, #589), an overlay with nothing behind it (B11, #589), and a
 * partner's client CV used as the caller's own (C03, #594).
 */
export const APPLY_ERROR_COPY_KEYS: Record<string, string> = {
  CV_RESOLUTION_FAILED: 'jobs.applyErrors.cvResolutionFailed',
  CV_EMPTY: 'jobs.applyErrors.cvEmpty',
  CV_BELONGS_TO_CLIENT: 'jobs.applyErrors.cvBelongsToClient',
  JOB_EXTERNAL_ONLY: 'jobs.applyErrors.jobExternalOnly',
  // The target company is not open to submissions through Tabbio (the
  // employer phase-1 gate, raised by the talent network join).
  FEATURE_DISABLED: 'jobs.applyErrors.notAccepting',
  SUBMISSION_DISCLOSURE_CHANGED: 'common:settings.employerAccess.retryReview',
  NETWORK_ERROR: 'common:errors.network',
};

/**
 * Refusals that another CV would fix. CV_RESOLUTION_FAILED is retryable as is
 * (nothing was written, the same request can simply be sent again), so it is
 * deliberately not here.
 */
export const CV_CHOICE_APPLY_ERROR_CODES: ReadonlySet<string> = new Set([
  'CV_EMPTY',
  'CV_BELONGS_TO_CLIENT',
]);

export function getApplyErrorCode(error: unknown): string | null {
  if (!error || typeof error !== 'object') return null;
  const code = (error as { code?: unknown }).code;
  return typeof code === 'string' && code ? code : null;
}

function getApplyErrorMessage(error: unknown): string | null {
  if (!error || typeof error !== 'object') return null;
  const message = (error as { message?: unknown }).message;
  return typeof message === 'string' && message.trim() ? message : null;
}

/** True when the server refused the chosen CV and a different one is the fix. */
export function isCvChoiceApplyError(error: unknown): boolean {
  const code = getApplyErrorCode(error);
  return code !== null && CV_CHOICE_APPLY_ERROR_CODES.has(code);
}

/**
 * The user-facing copy for an apply failure: translated copy when the server
 * code is one we know, the server message when it is not, and the caller's
 * fallback when there is no message at all.
 */
export function getApplyErrorCopy(
  error: unknown,
  t: (key: string) => string,
  fallback: string,
): string {
  const code = getApplyErrorCode(error);
  const key = code ? APPLY_ERROR_COPY_KEYS[code] : undefined;
  if (key) {
    const translated = t(key);
    // i18next returns the key itself when the string is missing.
    if (translated && translated !== key) return translated;
  }

  return getApplyErrorMessage(error) ?? fallback;
}
